import { PayloadAction, Slice, createSlice } from "@reduxjs/toolkit";
import { sanitizeSignerEmail, isTruthy } from "helpers/methods";
import {
  Approval,
  CustomField,
  FileField,
  MultiSignerUpdate,
  RequestFile,
  RequestParticipant,
  Reviewers,
  SignRequest,
  SignRequestErrors,
} from "models/interfaces";
import { normalizeOrders } from "utils/signerOrderUtils";
import { RootState } from "utils/store";

export type FieldHistorySnapshot = {
  fields: FileField[];
  selectedFieldId: number | null;
};

export type DocFieldHistory = {
  past: FieldHistorySnapshot[];
  future: FieldHistorySnapshot[];
};

export interface AppState {
  isMobile: boolean;
  loading: boolean;
  isRequest: boolean;
  signRequest: SignRequest;
  signRequester: RequestParticipant;
  selectedParticipant: RequestParticipant;
  selectedReviewer: RequestParticipant;
  selectedFile: RequestFile;
  selectedField: FileField;
  uploadedFileNames: string[];
  isBulkSend: boolean;
  uploadedCsvFileNames: string[];
  errorsBulkContact: RequestParticipant[];
  errors: SignRequestErrors;
  contacts: { name: string; email: string }[];
  isDriveIntegrated: boolean;
  driveFiles: any;
  isDrive: boolean;
  driveAccessToken: string;
  integrations: string[];
  selectedFieldDrawer: boolean;
  /** The sender's custom field library, loaded when the editor opens. */
  customFields: CustomField[];
  mobilePropertiesRequested: boolean;
  assignExistingFields: { [key: string]: boolean };
  goBackPopup: boolean;
  chipLoader: boolean;
  sideCollpased: boolean;
  historyByDoc: Record<string, DocFieldHistory>;
  clipboardField: FileField | null;
  hasAutoDetected: Record<string, boolean>;
  pendingAiFields: Record<string, FileField[]>;
  /**
   * The requester's account-level in-person consent — `null` until fetched.
   * Read once per session, from wherever an in-person signer is first added.
   */
  requesterInPersonConsented: boolean | null;
}

const initialState: AppState = {
  isMobile: false,
  loading: false,
  isRequest: true,
  isDriveIntegrated: true,
  isDrive: false,
  signRequest: {
    id: "",
    signers: [],
    files: [],
    expirationDays: 180,
    emailSubject: "",
    emailMessage: "",
    requestedBy: { name: "", email: "" },
    selfSigned: false,
    signerOrderRequired: false,
    signedBy: "",
    status: "",
    templateName: "",
    imagePath: "",
    scheduleTime: "",
    approval: {
      approvalNeeded: false,
      orderNeeded: false,
      reviewers: [],
    },
    reminders: {
      daysBeforeFirstReminder: 5,
      daysBetweenReminders: 3,
    },
    needReminders: false,
    signRequestConfig: {
      attachDocumentToMailOnCompletion: true,
      allowDocumentDownload: true,
      sendNotificationOnCompletion: true,
      attachSingleCertificate: true,
      allowSignatureDelegation: true,
      inPersonHostEmail: "",
    },
    currentSigners: [],
    requestToken: "",
    autoGeneratedId: 0,
    templateId: null,
  },
  signRequester: {
    name: "",
    email: "",
    requestRole: "",
    delayDays: 0,
    delayHours: 0,
    cc: false,
    id: 0,
    order: 0,
    color: "",
    companyName: "",
    title: "",
  },
  selectedParticipant: {
    id: 0,
    name: "",
    email: "",
    order: 0,
    color: "",
    cc: false,
    requestRole: "",
    companyName: "",
    title: "",
  },
  selectedReviewer: {
    id: 0,
    name: "",
    email: "",
    order: 0,
    color: "",
    cc: false,
    requestRole: "",
    companyName: "",
    title: "",
  },
  selectedFile: {
    unsignedDoc: "",
    unsignedDocUrl: "",
    signedDoc: "",
    fields: [],
    pageCount: 0,
    pageUrls: {},
    dimensions: {},
  },
  selectedField: {
    id: 0,
    name: "",
    type: "",
    value: "",
    frameX: 0,
    frameY: 0,
    x: 0,
    y: 0,
    w: 0,
    h: 0,
    width: 0,
    height: 0,
    color: "",
    page: 0,
    signerIndex: 0,
    maxLength: 0,
    required: false,
    hidden: false,
    readOnly: false,
    tabLabel: "",
    label: "",
    toolTip: "",
    validationPattern: "none",
    validationMessage: "",
    placeholder: "",
    multiLine: false,
    styles: {
      fontFamily: "none",
      color: "",
      size: 16,
      fontWeight: {
        bold: false,
        italic: false,
        underline: false,
      },
    },
    group: [],
    top: 0,
    left: 0,
  },
  uploadedFileNames: [],
  isBulkSend: false,
  uploadedCsvFileNames: [],
  errorsBulkContact: [],
  errors: {
    file: "",
    name: "",
    email: "",
    order: "",
    templateName: "",
  },
  contacts: [],
  driveFiles: [],
  driveAccessToken: "",
  integrations: [],
  selectedFieldDrawer: false,
  customFields: [],
  mobilePropertiesRequested: false,
  assignExistingFields: {},
  goBackPopup: false,
  chipLoader: false,
  sideCollpased: false,
  historyByDoc: {},
  clipboardField: null,
  hasAutoDetected: {},
  pendingAiFields: {},
  requesterInPersonConsented: null,
};

const getDocId = (file: { unsignedDoc?: string; unsignedPdf?: string }) =>
  file?.unsignedDoc || file?.unsignedPdf || "";

const ensureDocHistory = (state: AppState, docId: string): DocFieldHistory => {
  if (!state.historyByDoc) {
    state.historyByDoc = {};
  }
  if (!state.historyByDoc[docId]) {
    state.historyByDoc[docId] = { past: [], future: [] };
  }
  return state.historyByDoc[docId];
};

const writeFieldsToDoc = (
  state: AppState,
  docId: string,
  fields: FileField[],
) => {
  state.signRequest.files = state.signRequest.files.map((file: RequestFile) =>
    getDocId(file) === docId ? { ...file, fields } : file,
  );
  if (getDocId(state.selectedFile) === docId) {
    state.selectedFile.fields = fields;
  }
};

const refreshSelectedField = (
  state: AppState,
  fields: FileField[],
  selectedFieldId: number | null,
) => {
  if (selectedFieldId == null) {
    state.selectedField = initialState.selectedField;
    return;
  }
  const found = fields.find((f) => f.id === selectedFieldId);
  if (found) {
    state.selectedField = { ...state.selectedField, ...found };
  } else {
    state.selectedField = initialState.selectedField;
    state.selectedFieldDrawer = false;
  }
};

const syncFieldWithSigner = (signRequest: SignRequest) => {
  if (!signRequest.files || !signRequest.signers) return;
  // NOTE: rebuild files/fields immutably instead of mutating in place. The
  // incoming field objects can be frozen (e.g. loaded from a template or a
  // previously frozen Immer state), so a direct `field.value = ...` assignment
  // throws "Cannot assign to read only property 'value'" and aborts the caller
  // (which was silently blocking document uploads when a template was used).
  signRequest.files = signRequest.files.map((file: RequestFile) => {
    if (!file.fields) return file;
    return {
      ...file,
      fields: file.fields.map((field: FileField) => {
        if (field.type === "email") {
          const signer = signRequest.signers.find(
            (s) => s.id === field.signerIndex,
          );
          if (signer) {
            return { ...field, value: signer.email || "" };
          }
        }
        return field;
      }),
    };
  });
};

/**
 * Keep the editor's selected participant in step with an edit to the signer it
 * points at.
 *
 * `selectedParticipant` holds a *copy* taken when the participant was picked,
 * not a live reference, so an edit here leaves the editor working from a stale
 * version of that recipient. That is merely untidy for a name or an email — but
 * it is a correctness bug for `cc`: a recipient who only receives a copy signs
 * nothing, is excluded from the editor's own recipient list, and owns no
 * fields. Leaving them selected stamps every subsequently placed field with a
 * participant the editor no longer lists, so the real signers end up with no
 * signature field and the step fails on "add at least one signature field for
 * each signer" no matter how many fields were placed.
 *
 * Clearing the selection lets the editor's auto-select pick a signable
 * participant again, the same way the delete-signer paths already do.
 */
const syncSelectedParticipant = (
  state: AppState,
  signerId: number,
  update: Partial<RequestParticipant>,
) => {
  if (state.selectedParticipant?.id !== signerId) return;
  // Keyed off what the signer now IS, read back from the already-updated
  // signers array, rather than off whatever the caller's partial happened to
  // carry. A partial that omits `cc` says nothing about the recipient's role,
  // so testing the partial would leave a selection pointing at a CC intact
  // whenever the edit was about something else; this way any edit to a
  // copy-only recipient drops the selection, whatever the edit was.
  const updated = state.signRequest.signers.find(
    (signer: RequestParticipant) => signer.id === signerId,
  );
  state.selectedParticipant = updated?.cc
    ? { ...initialState.selectedParticipant }
    : { ...state.selectedParticipant, ...update };
};

export const appSlice: Slice<AppState> = createSlice({
  name: "app",
  initialState,
  reducers: {
    setScreen: (state, action: PayloadAction<boolean>) => {
      state.isMobile = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setIsRequest: (state, action: PayloadAction<boolean>) => {
      state.isRequest = action.payload;
    },
    setIntegrations: (state, action: any) => {
      state.integrations = action.payload;
    },
    setDriveFiles: (state, action: any) => {
      state.driveFiles = Object.values(action.payload);
    },
    setDriveAccessToken: (state, action: any) => {
      state.driveAccessToken = action.payload;
    },
    setIsDriveIntegrated: (state, action: any) => {
      state.isDriveIntegrated = action.payload;
    },
    setIsDrive: (state, action: any) => {
      state.isDrive = action.payload;
    },
    setReduxSignRequest: (
      state,
      action: PayloadAction<Partial<SignRequest> | undefined>,
    ) => {
      if (action.payload) {
        state.signRequest = { ...state.signRequest, ...action.payload };

        if (state.signRequest.files) {
          state.uploadedFileNames = state.signRequest.files.map(
            (file: any) => file.unsignedDoc || file.fileName || file.name || "",
          );
        }
        syncFieldWithSigner(state.signRequest);
      } else {
        state.isRequest = initialState.isRequest;
        state.signRequest = initialState.signRequest;
        state.selectedParticipant = initialState.selectedParticipant;
        state.selectedFile = initialState.selectedFile;
        state.uploadedFileNames = initialState.uploadedFileNames;
        state.errors = initialState.errors;
        state.isBulkSend = initialState.isBulkSend;
        state.uploadedCsvFileNames = initialState.uploadedCsvFileNames;
        state.errorsBulkContact = initialState.errorsBulkContact;
        state.hasAutoDetected = initialState.hasAutoDetected;
        state.pendingAiFields = initialState.pendingAiFields;
      }
    },
    setPdfRequester: (state, action: PayloadAction<RequestParticipant>) => {
      state.signRequester = action.payload;
    },
    setReduxPdfApprovals: (
      state,
      action: PayloadAction<Partial<Approval> | undefined>,
    ) => {
      if (action.payload) {
        const updatedPdfApprovals = {
          ...state.signRequest.approval,
          ...action.payload,
        };
        updatedPdfApprovals.approvalNeeded = checkApprovalNeeded(
          updatedPdfApprovals.reviewers,
        );

        state.signRequest.approval = updatedPdfApprovals;
      } else {
        state.signRequest.approval = initialState.signRequest.approval;
        state.selectedReviewer = initialState.selectedReviewer;
        state.errors = initialState.errors;
      }
    },
    moveFileSignRequest(state, action) {
      const { fromIndex, toIndex } = action.payload;
      const [movedFile] = state.signRequest.files.splice(fromIndex, 1);
      state.signRequest.files.splice(toIndex, 0, movedFile);
      state.selectedFile = state.signRequest.files[0];
    },
    updateFileInSignRequest: (
      state,
      action: PayloadAction<Partial<RequestFile>>,
    ) => {
      // Update the specific file inside signRequest.files
      state.signRequest.files = state.signRequest.files.map(
        (file: RequestFile) =>
          file.unsignedDoc === action.payload.unsignedDoc
            ? { ...file, ...action.payload }
            : file,
      );

      // If the currently selected file is the one being updated, merge changes into it.
      // Otherwise, preserve the current selection as-is.
      if (
        action.payload.unsignedDoc &&
        state.selectedFile.unsignedDoc === action.payload.unsignedDoc
      ) {
        state.selectedFile = {
          ...state.selectedFile,
          ...action.payload,
        } as RequestFile;
      }
    },
    updateFileWithFields: (
      state,
      action: PayloadAction<Partial<RequestFile>>,
    ) => {
      state.signRequest.files = state.signRequest.files.map(
        (file: RequestFile) => {
          if (file.unsignedDoc === action.payload.unsignedDoc) {
            // If payload brings fresh fields (e.g. from upload API), keep them intact.
            if (action.payload.fields && action.payload.fields.length > 0) {
              file = { ...file, ...action.payload };
            } else {
              // Otherwise filter existing fields to current signers.
              const filteredFields = file.fields.filter((field: FileField) => {
                if (field.existingField) return true;
                // Any-signer custom fields (signerIndex -1) belong to no one
                // in particular, so a signer change never drops them.
                if (field.signerIndex === -1) return true;
                return state.signRequest.signers
                  .filter((_) => !_.cc)
                  .find(
                    (signer: RequestParticipant) =>
                      signer.id === field.signerIndex,
                  );
              });
              file = { ...file, ...action.payload, fields: filteredFields };
            }
          }
          return file;
        },
      );
      // Keep selectedFile in sync when the same file is updated
      if (
        action.payload.unsignedDoc &&
        state.selectedFile.unsignedDoc === action.payload.unsignedDoc
      ) {
        state.selectedFile =
          state.signRequest.files.find(
            (file) => file.unsignedDoc === state.selectedFile.unsignedDoc,
          ) || initialState.selectedFile;
      }
    },
    addFileInSignRequest: (
      state,
      action: PayloadAction<Partial<RequestFile>>,
    ) => {
      state.signRequest.files.push({
        ...initialState.selectedFile,
        ...action.payload,
      });
    },
    removeFileFromSignRequest: (
      state,
      action: PayloadAction<Partial<RequestFile>>,
    ) => {
      const filteredFiles = state.signRequest.files.filter(
        (file) => file.unsignedDoc !== action.payload.unsignedDoc,
      );
      state.signRequest.files = filteredFiles;

      const wasSelectedDeleted =
        action.payload.unsignedDoc === state.selectedFile.unsignedDoc;

      if (!filteredFiles.length) {
        state.selectedFile = initialState.selectedFile;
      } else if (wasSelectedDeleted) {
        state.selectedFile = filteredFiles[0];
      }
      // else: preserve current selection

      if (action.payload.unsignedDoc) {
        if (state.hasAutoDetected) {
          delete state.hasAutoDetected[action.payload.unsignedDoc];
        }
        if (state.pendingAiFields) {
          delete state.pendingAiFields[action.payload.unsignedDoc];
        }
      }
    },
    setReduxSelectedParticipant: (
      state,
      action: PayloadAction<Partial<RequestParticipant | undefined>>,
    ) => {
      if (action.payload) {
        state.selectedParticipant = {
          ...state.selectedParticipant,
          ...action.payload,
        };
      } else {
        state.selectedParticipant =
          state?.signRequest?.signers?.filter(
            (signer: RequestParticipant) => !signer.cc,
          )[0] || initialState.selectedParticipant;
      }
    },
    updateIndividualSigner: (
      state,
      action: PayloadAction<{
        signerId: number;
        fieldsToUpdate: Partial<RequestParticipant>;
      }>,
    ) => {
      const fieldsToUpdate = { ...action.payload.fieldsToUpdate };
      if (typeof fieldsToUpdate.email === "string") {
        fieldsToUpdate.email =
          sanitizeSignerEmail(fieldsToUpdate.email) || fieldsToUpdate.email;
      }
      state.signRequest.signers = state.signRequest.signers.map(
        (signer: RequestParticipant) => {
          if (signer.id === action.payload.signerId) {
            signer = { ...signer, ...fieldsToUpdate };
          }
          return signer;
        },
      );
      syncSelectedParticipant(state, action.payload.signerId, fieldsToUpdate);
      syncFieldWithSigner(state.signRequest);
    },
    updateMultipleSigners: (
      state,
      action: PayloadAction<MultiSignerUpdate[]>,
    ) => {
      const merged = new Map<number, Partial<RequestParticipant>>();

      action.payload.forEach(({ signerId, fieldsToUpdate }) => {
        merged.set(signerId, {
          ...merged.get(signerId),
          ...fieldsToUpdate,
        });
      });

      state.signRequest.signers = state.signRequest.signers.map(
        (signer: RequestParticipant) => {
          const update = merged.get(signer.id);
          if (!update) return signer;
          const sanitizedUpdate = { ...update };
          if (typeof sanitizedUpdate.email === "string") {
            sanitizedUpdate.email =
              sanitizeSignerEmail(sanitizedUpdate.email) ||
              sanitizedUpdate.email;
          }
          return {
            ...signer,
            ...sanitizedUpdate,

            // important: clears delay when undefined (ungroup case)
            delayDays: update.delayDays ?? undefined,
            delayHours: update.delayHours ?? undefined,
            scheduleTime: update.scheduleTime ?? undefined,
          };
        },
      );

      merged.forEach((update, signerId) =>
        syncSelectedParticipant(state, signerId, update),
      );
    },
    mergeReviewerToGroup: (
      state,
      action: PayloadAction<{
        draggedId: number;
        targetId: number;
      }>,
    ) => {
      const { draggedId, targetId } = action.payload;
      const reviewers = state.signRequest.approval.reviewers;

      const dragged = reviewers.find((r) => r.id === draggedId);
      const target = reviewers.find((r) => r.id === targetId);
      if (!dragged || !target) return;

      //  cannot merge into a reviewer without group
      if (!target.group) return;

      // already same group → do nothing
      if (dragged.group === target.group) return;

      // merge into existing group
      dragged.group = target.group;
      dragged.order = target.order;
    },
    updateSignerOrders: (
      state,
      action: PayloadAction<RequestParticipant[]>,
    ) => {
      const map = new Map(action.payload.map((s) => [s.id, s.order]));

      state.signRequest.signers = state.signRequest.signers.map((signer) =>
        map.has(signer.id) ? { ...signer, order: map.get(signer.id)! } : signer,
      );
    },
    updateReviewerOrders: (state, action: PayloadAction<Reviewers[]>) => {
      const map = new Map(
        action.payload.map((r) => [r.id, { order: r.order, group: r.group }]),
      );

      state.signRequest.approval.reviewers =
        state.signRequest.approval.reviewers.map((reviewer) => {
          const patch = map.get(reviewer.id);
          if (!patch) return reviewer;

          if ("group" in patch && patch.group === undefined) {
            const { group, ...rest } = reviewer;
            return { ...rest, order: patch.order };
          }

          return {
            ...reviewer,
            order: patch.order,
            ...("group" in patch && { group: patch.group }),
          };
        });
    },
    moveGroup: (
      state,
      action: PayloadAction<{
        signerIds: number[];
        toOrder: number;
      }>,
    ) => {
      const { signerIds, toOrder } = action.payload;

      // 1️ Get all signers in the dragged group
      const groupSigners = state.signRequest.signers.filter((s) =>
        signerIds.includes(s.id),
      );

      if (!groupSigners.length) return;

      // All group members share the same order
      const fromOrder = groupSigners[0].order;

      // No-op if dropped on same block
      if (fromOrder === toOrder) return;

      // 2️ Shift other blocks
      state.signRequest.signers.forEach((s) => {
        if (fromOrder > toOrder) {
          // moving UP
          if (s.order >= toOrder && s.order < fromOrder) {
            s.order += 1;
          }
        } else {
          // moving DOWN
          if (s.order > fromOrder && s.order <= toOrder) {
            s.order -= 1;
          }
        }
      });

      // 3️ Place the whole group at target order
      groupSigners.forEach((s) => {
        s.order = toOrder;
      });
    },
    moveReviewerGroup: (
      state,
      action: PayloadAction<{
        reviewerIds: number[];
        toOrder: number;
      }>,
    ) => {
      const { reviewerIds, toOrder } = action.payload;

      // 1️1 Get all reviewers in dragged group
      const groupReviewers = state.signRequest.approval.reviewers.filter((r) =>
        reviewerIds.includes(r.id),
      );

      if (!groupReviewers.length) return;

      // all members share same order
      const fromOrder = groupReviewers[0].order;

      // no-op
      if (fromOrder === toOrder) return;

      // 2️ Shift other blocks
      state.signRequest.approval.reviewers.forEach((r) => {
        if (fromOrder > toOrder) {
          // moving UP
          if (r.order >= toOrder && r.order < fromOrder) {
            r.order += 1;
          }
        } else {
          // moving DOWN
          if (r.order > fromOrder && r.order <= toOrder) {
            r.order -= 1;
          }
        }
      });

      // 3️ Place whole group
      groupReviewers.forEach((r) => {
        r.order = toOrder;
      });
    },
    moveIndividualSigner(state, action) {
      const { fromIndex, toIndex } = action.payload;
      const [movedSigner] = state.signRequest.signers.splice(fromIndex, 1);
      state.signRequest.signers.splice(toIndex, 0, movedSigner);
    },
    moveSignerBlock: (
      state,
      action: {
        payload: {
          signerId: number;
          toOrder: number;
        };
      },
    ) => {
      const { signerId, toOrder } = action.payload;

      // signer being moved
      const draggedSigner = state.signRequest.signers.find(
        (s) => s.id === signerId,
      );
      if (!draggedSigner) return;

      const fromOrder = Number(draggedSigner.order);
      if (fromOrder === toOrder) return;

      // 1️ Shift other blocks
      state.signRequest.signers.forEach((s) => {
        const order = Number(s.order);

        // moving UP (e.g. 2 → 1)
        if (fromOrder > toOrder) {
          if (order >= toOrder && order < fromOrder) {
            s.order = order + 1;
          }
        }

        // moving DOWN (e.g. 1 → 3)
        if (fromOrder < toOrder) {
          if (order > fromOrder && order <= toOrder) {
            s.order = order - 1;
          }
        }
      });

      // 2️ Place dragged signer
      draggedSigner.order = toOrder;

      // 3️ Normalize orders
      const uniqueOrders = Array.from(
        new Set(state.signRequest.signers.map((s) => Number(s.order))),
      ).sort((a, b) => a - b);

      uniqueOrders.forEach((order, index) => {
        state.signRequest.signers
          .filter((s) => Number(s.order) === order)
          .forEach((s) => {
            s.order = index + 1;
          });
      });
    },
    moveReviewerBlock: (
      state,
      action: {
        payload: {
          reviewerId: number;
          toOrder: number;
        };
      },
    ) => {
      const { reviewerId, toOrder } = action.payload;

      const reviewers = state.signRequest.approval.reviewers;

      // reviewer being moved
      const draggedReviewer = reviewers.find((r) => r.id === reviewerId);
      if (!draggedReviewer) return;

      const fromOrder = Number(draggedReviewer.order);
      if (fromOrder === toOrder) return;

      // 1️ Shift other reviewers
      reviewers.forEach((r) => {
        const order = Number(r.order);

        // moving UP (3 → 1)
        if (fromOrder > toOrder) {
          if (order >= toOrder && order < fromOrder) {
            r.order = order + 1;
          }
        }

        // moving DOWN (1 → 3)
        if (fromOrder < toOrder) {
          if (order > fromOrder && order <= toOrder) {
            r.order = order - 1;
          }
        }
      });

      // 2️ Place dragged reviewer
      draggedReviewer.order = toOrder;

      // 3️ Normalize orders (remove gaps / duplicates)
      const uniqueOrders = Array.from(
        new Set(reviewers.map((r) => Number(r.order))),
      ).sort((a, b) => a - b);

      uniqueOrders.forEach((order, index) => {
        reviewers
          .filter((r) => Number(r.order) === order)
          .forEach((r) => {
            r.order = index + 1;
          });
      });
    },
    deleteSigner: (state, action: PayloadAction<{ signerId: number }>) => {
      const remaining = state.signRequest.signers.filter(
        (s) => s.id !== action.payload.signerId,
      );

      state.signRequest.signers = normalizeOrders(remaining);
      syncFieldWithSigner(state.signRequest);

      if (remaining.length === 0) {
        state.errors = { ...state.errors, name: "", email: "", order: "" };
      }

      if (state.selectedParticipant.id === action.payload.signerId) {
        state.selectedParticipant = { ...initialState.selectedParticipant };
      }
    },
    updateIndividualReviewer: (
      state,
      action: PayloadAction<{
        reviewerId: number;
        fieldsToUpdate: Partial<RequestParticipant>;
      }>,
    ) => {
      state.signRequest.approval.reviewers =
        state.signRequest.approval.reviewers.map(
          (reviewer: RequestParticipant) => {
            if (reviewer.id === action.payload.reviewerId) {
              reviewer = { ...reviewer, ...action.payload.fieldsToUpdate };
            }
            return reviewer;
          },
        );
      state.signRequest.approval.approvalNeeded = checkApprovalNeeded(
        state.signRequest.approval.reviewers,
      );
    },

    moveIndividualReviewer(state, action) {
      const { fromIndex, toIndex } = action.payload;
      const [movedReviewer] = state.signRequest.approval.reviewers.splice(
        fromIndex,
        1,
      );
      state.signRequest.approval.reviewers.splice(toIndex, 0, movedReviewer);
      state.signRequest.approval.approvalNeeded = checkApprovalNeeded(
        state.signRequest.approval.reviewers,
      );
    },

    deleteReviewer: (
      state,
      action: PayloadAction<{
        reviewerId: number;
      }>,
    ) => {
      const newReviewersList = state.signRequest.approval.reviewers.filter(
        (reviewer: RequestParticipant) =>
          reviewer.id !== action.payload.reviewerId,
      );
      state.signRequest.approval.reviewers = newReviewersList;
      state.signRequest.approval.approvalNeeded =
        checkApprovalNeeded(newReviewersList);

      if (newReviewersList.length === 0) {
        state.errors = { ...state.errors, name: "", email: "", order: "" };
      }
      if (state.selectedReviewer.id === action.payload.reviewerId) {
        state.selectedReviewer = { ...initialState.selectedReviewer };
      }
    },

    deleteMultipleReviewer: (
      state,
      action: PayloadAction<RequestParticipant[]>,
    ) => {
      state.signRequest.approval.reviewers = action.payload;
      state.signRequest.approval.approvalNeeded = checkApprovalNeeded(
        action.payload,
      );

      if (action.payload.length === 0) {
        state.errors = { ...state.errors, name: "", email: "", order: "" };
      }

      if (!action.payload.some((r) => r.id === state.selectedReviewer.id)) {
        state.selectedReviewer = { ...initialState.selectedReviewer };
      }
    },
    removeFieldsOfCC: (
      state,
      action: PayloadAction<{
        signerId: number;
      }>,
    ) => {
      const files = state.signRequest.files.map((file: RequestFile) => {
        const fields = file.fields.filter(
          (field: FileField) => field.signerIndex !== action.payload.signerId,
        );
        file.fields = fields;
        return file;
      });
      state.signRequest.files = files;
      state.selectedFile.fields = state.selectedFile.fields.filter(
        (field: FileField) => field.signerIndex !== action.payload.signerId,
      );
    },

    setReduxSelectedFile: (
      state,
      action: PayloadAction<RequestFile | null>,
    ) => {
      // check if payload is null
      if (!action.payload) {
        state.selectedFile = initialState.selectedFile;
        return;
      }
      if (!state.signRequest.files) {
        return;
      }
      //check if file exists in signRequest
      const fileExists = state.signRequest.files.find(
        (file) => file.unsignedDoc === action.payload?.unsignedDoc,
      );
      if (fileExists) {
        state.selectedFile.unsignedDoc = action.payload.unsignedDoc;
        state.selectedFile.unsignedDocUrl = action.payload.unsignedDocUrl;
        state.selectedFile.unsignedPdf =
          fileExists.unsignedPdf || action.payload.unsignedPdf;
        state.selectedFile.unsignedPdfUrl =
          fileExists.unsignedPdfUrl || action.payload.unsignedPdfUrl;
        state.selectedFile.signedDoc = action.payload.signedDoc;
        state.selectedFile.fields = action.payload.fields;
        state.selectedFile.pageCount = action.payload.pageCount;
        state.selectedFile.pageUrls = {
          ...fileExists.pageUrls,
          ...action.payload.pageUrls,
        };
        // Only merge dimensions when at least one side actually has them.
        // Spreading two undefined values yields `{}` — a truthy-but-useless
        // value that makes callers believe dimensions are loaded and stop
        // retrying, leaving existing fields with no real coordinates.
        if (
          isTruthy(fileExists.dimensions) ||
          isTruthy(action.payload.dimensions)
        ) {
          state.selectedFile.dimensions = {
            ...fileExists.dimensions,
            ...action.payload.dimensions,
          };
        }
      }
    },
    /**
     * Patches the field the Properties panel is showing. Merges, so callers can send just the
     * keys that changed. Use {@link selectField} when the selection MOVES to another field --
     * merging there carries every key the new field happens to omit over from the old one.
     */
    setSelectedField: (
      state,
      action: PayloadAction<Partial<FileField> | null>,
    ) => {
      if (action.payload) {
        state.selectedField = { ...state.selectedField, ...action.payload };
      } else {
        state.selectedField = initialState.selectedField;
      }
    },
    /**
     * Moves the selection to a different field. Starts from the initial shape rather than the
     * previously selected field, so nothing leaks between fields: a field that has no
     * `customFieldId` or `defaultValue` must not inherit the last field's.
     */
    selectField: (state, action: PayloadAction<Partial<FileField> | null>) => {
      state.selectedField = action.payload
        ? { ...initialState.selectedField, ...action.payload }
        : initialState.selectedField;
    },
    deleteFieldOfSelectedFile: (state, action: PayloadAction<FileField>) => {
      const updatedFields = state.selectedFile.fields.filter(
        (field: FileField) => field.id !== action.payload.id,
      );
      state.selectedFile.fields = updatedFields;
      for (let file of state.signRequest.files) {
        if (file.unsignedDoc === state.selectedFile.unsignedDoc) {
          file.fields = updatedFields;
        }
      }
      if (action.payload.id === state.selectedField.id) {
        state.selectedField = initialState.selectedField;
        state.selectedFieldDrawer = false;
      }
    },
    updateFieldOfSelectedFile: (state, action: PayloadAction<Partial<any>>) => {
      const updatedFields = state.selectedFile.fields.map((field: any) => {
        if (field.id === action.payload.id) {
          const { styles, ...payloadWithoutStyles } = action.payload;
          field = { ...field, ...payloadWithoutStyles };
          field.styles = styles || field.styles;
          return field;
        } else return field;
      });
      state.selectedFile.fields = updatedFields;
    },
    updateFieldInSignRequest: (
      state,
      action: PayloadAction<Partial<FileField>>,
    ) => {
      state.signRequest.files = state.signRequest.files.map(
        (file: RequestFile) => {
          if (file.unsignedDoc === state.selectedFile.unsignedDoc) {
            file.fields = file.fields.map((field: FileField) => {
              if (field.id === action.payload.id) {
                field = { ...field, ...action.payload };
              }
              return field;
            });
          }
          return file;
        },
      );
    },
    setRadioGroupValue: (
      state,
      action: PayloadAction<{ groupId: string; selectedFieldId: number }>,
    ) => {
      const { groupId, selectedFieldId } = action.payload;
      const apply = (field: FileField): FileField => {
        if (field.type === "radio_button" && field.groupId === groupId) {
          return {
            ...field,
            value: field.id === selectedFieldId ? "true" : "false",
          };
        }
        return field;
      };
      state.selectedFile.fields = state.selectedFile.fields.map(apply);
      state.signRequest.files = state.signRequest.files.map(
        (file: RequestFile) => {
          if (file.unsignedDoc === state.selectedFile.unsignedDoc) {
            file.fields = file.fields.map(apply);
          }
          return file;
        },
      );
    },
    assignExistingFieldsToSigner: (
      state,
      action: PayloadAction<RequestParticipant>,
    ) => {
      const updatedFields = state.selectedFile.fields.map(
        (field: FileField) => {
          field.signerIndex = action.payload.id;
          field.color = action.payload.color;
          return field;
        },
      );
      state.selectedFile.fields = updatedFields;
      for (let file of state.signRequest.files) {
        if (file.unsignedDoc === state.selectedFile.unsignedDoc) {
          file.fields = updatedFields;
        }
      }
    },
    updateSelfSignedFlag: (state, action: PayloadAction<boolean>) => {
      state.signRequest.selfSigned = action.payload;
    },
    setUploadedFileNames: (state, action: PayloadAction<string[]>) => {
      const newNames = action.payload.filter(
        (name) => name && !state.uploadedFileNames.includes(name),
      );
      state.uploadedFileNames = [...state.uploadedFileNames, ...newNames];

      const emailSubject = state.uploadedFileNames.length
        ? "Sign with Inkfree" + `: ${state.uploadedFileNames.join(", ")}`
        : "";
      if (emailSubject.includes(state.signRequest.emailSubject)) {
        state.signRequest.emailSubject =
          emailSubject.length > 100
            ? `${emailSubject.slice(0, 97).trim()}...`
            : emailSubject;
      }
    },
    setUploadedFileNamesDelete: (state, action: PayloadAction<string>) => {
      state.uploadedFileNames = state.uploadedFileNames.filter(
        (fileName: string) => fileName !== action.payload,
      );
      const emailSubject =
        "Sign with Inkfree" + `: ${state.uploadedFileNames.join(", ")}`;

      if (state.signRequest.emailSubject.includes(emailSubject)) {
        state.signRequest.emailSubject =
          emailSubject.length > 100
            ? `${emailSubject.slice(0, 97).trim()}...`
            : emailSubject;
      }
    },
    setErrors: (
      state,
      action: PayloadAction<SignRequestErrors | undefined>,
    ) => {
      if (action.payload) {
        state.errors = { ...state.errors, ...action.payload };
      } else {
        state.errors = initialState.errors;
      }
    },
    setContacts: (
      state,
      action: PayloadAction<{ name: string; email: string }[]>,
    ) => {
      state.contacts = action.payload;
    },
    setScheduleTime: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload) {
        state.signRequest.scheduleTime = action.payload;
      } else {
        state.signRequest.scheduleTime = "";
      }
    },
    updateStatusBulkSend: (state, action: PayloadAction<boolean>) => {
      state.isBulkSend = action.payload;
    },
    setUploadedCsvFileName: (state, action: PayloadAction<string[]>) => {
      if (action.payload.length === 0) state.uploadedCsvFileNames = [];
      else
        state.uploadedCsvFileNames = [
          ...state.uploadedCsvFileNames,
          ...action.payload,
        ];
    },
    addAllErrorCsvSigner: (state, action: PayloadAction<any>) => {
      if (action.payload) {
        let array: any = action.payload;
        let RequestParticipants: RequestParticipant[] = [];
        array.forEach((signer: any, index: number) => {
          RequestParticipants.push({
            id: index,
            name: signer.name,
            email: sanitizeSignerEmail(signer.email) || signer.email,
            order: 0,
            color: "#D2DE32",
            cc: false,
            requestRole: signer.requestRole,
            companyName: signer.companyName,
            title: signer.title,
          });
        });
        state.errorsBulkContact = RequestParticipants;
      } else {
        state.errorsBulkContact = [];
      }
    },

    updateIndividualErrorSigner: (
      state,
      action: PayloadAction<{
        signerId: number;
        fieldsToUpdate: Partial<RequestParticipant>;
      }>,
    ) => {
      state.errorsBulkContact = state.errorsBulkContact.map(
        (signer: RequestParticipant) => {
          if (signer.id === action.payload.signerId) {
            signer = { ...signer, ...action.payload.fieldsToUpdate };
          }
          return signer;
        },
      );
    },
    deleteErrorSigner: (
      state,
      action: PayloadAction<{
        signerId: number;
      }>,
    ) => {
      state.errorsBulkContact = state.errorsBulkContact.filter(
        (signer: RequestParticipant) => signer.id !== action.payload.signerId,
      );
      if (state.selectedParticipant.id === action.payload.signerId) {
        state.selectedParticipant = { ...initialState.selectedParticipant };
      }
    },
    setSelectedFieldDrawer: (state, action: PayloadAction<boolean>) => {
      state.selectedFieldDrawer = action.payload;
    },
    setCustomFields: (state, action: PayloadAction<CustomField[]>) => {
      state.customFields = action.payload;
    },
    upsertCustomField: (state, action: PayloadAction<CustomField>) => {
      // State rehydrated from an older localStorage blob has no library yet.
      if (!Array.isArray(state.customFields)) state.customFields = [];
      const index = state.customFields.findIndex(
        (field) => field.id === action.payload.id,
      );
      if (index >= 0) {
        state.customFields[index] = action.payload;
      } else {
        state.customFields.push(action.payload);
      }
      state.customFields.sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      );
    },
    removeCustomField: (state, action: PayloadAction<string>) => {
      state.customFields = (state.customFields ?? []).filter(
        (field) => field.id !== action.payload,
      );
    },
    setMobilePropertiesRequested: (state, action: PayloadAction<boolean>) => {
      state.mobilePropertiesRequested = action.payload;
    },
    setAssignExistingFields: (
      state,
      action: PayloadAction<{ fileName: string; fieldsExist: boolean }>,
    ) => {
      state.assignExistingFields[action.payload.fileName] =
        action.payload.fieldsExist;
    },
    setGoBackPopup: (state, action: PayloadAction<boolean>) => {
      state.goBackPopup = action.payload;
    },
    setChipLoader: (state, action: PayloadAction<boolean>) => {
      state.chipLoader = action.payload;
    },
    setSideCollpased: (state, action: PayloadAction<boolean>) => {
      state.sideCollpased = action.payload;
    },
    pushHistorySnapshot: (
      state,
      action: PayloadAction<{ docId: string; snapshot: FieldHistorySnapshot }>,
    ) => {
      const { docId, snapshot } = action.payload;
      if (!docId) return;
      const stack = ensureDocHistory(state, docId);
      stack.past.push(snapshot);
      // Cap undo depth so history can never grow unbounded (backstop against a
      // buggy caller pushing snapshots in a loop — that previously grew the JS
      // heap until the tab ran out of memory).
      const MAX_HISTORY = 50;
      if (stack.past.length > MAX_HISTORY) {
        stack.past.splice(0, stack.past.length - MAX_HISTORY);
      }
      stack.future = [];
    },
    undoFieldChange: (state, action: PayloadAction<{ docId: string }>) => {
      const { docId } = action.payload;
      const stack = state.historyByDoc?.[docId];
      if (!stack || stack.past.length === 0) return;

      const file = state.signRequest.files.find(
        (f: RequestFile) => getDocId(f) === docId,
      );
      const currentFields = file ? file.fields : state.selectedFile.fields;

      stack.future.push({
        fields: JSON.parse(JSON.stringify(currentFields)),
        selectedFieldId: state.selectedField?.id || null,
      });

      const previous = stack.past.pop()!;
      writeFieldsToDoc(state, docId, previous.fields);

      if (getDocId(state.selectedFile) === docId) {
        refreshSelectedField(state, previous.fields, previous.selectedFieldId);
      }
    },
    redoFieldChange: (state, action: PayloadAction<{ docId: string }>) => {
      const { docId } = action.payload;
      const stack = state.historyByDoc?.[docId];
      if (!stack || stack.future.length === 0) return;

      const file = state.signRequest.files.find(
        (f: RequestFile) => getDocId(f) === docId,
      );
      const currentFields = file ? file.fields : state.selectedFile.fields;

      stack.past.push({
        fields: JSON.parse(JSON.stringify(currentFields)),
        selectedFieldId: state.selectedField?.id || null,
      });

      const next = stack.future.pop()!;
      writeFieldsToDoc(state, docId, next.fields);

      if (getDocId(state.selectedFile) === docId) {
        refreshSelectedField(state, next.fields, next.selectedFieldId);
      }
    },
    clearFieldHistory: (state) => {
      state.historyByDoc = {};
    },
    setClipboardField: (state, action: PayloadAction<FileField | null>) => {
      state.clipboardField = action.payload
        ? (JSON.parse(JSON.stringify(action.payload)) as FileField)
        : null;
    },
    setHasAutoDetected: (
      state,
      action: PayloadAction<{ docId: string; detected: boolean }>,
    ) => {
      if (!state.hasAutoDetected) {
        state.hasAutoDetected = {};
      }
      state.hasAutoDetected[action.payload.docId] = action.payload.detected;
    },
    setPendingAiFields: (
      state,
      action: PayloadAction<{ docId: string; fields: FileField[] }>,
    ) => {
      if (!state.pendingAiFields) {
        state.pendingAiFields = {};
      }
      state.pendingAiFields[action.payload.docId] = action.payload.fields;
    },
    clearPendingAiFields: (state, action: PayloadAction<{ docId: string }>) => {
      if (state.pendingAiFields) {
        delete state.pendingAiFields[action.payload.docId];
      }
    },
    setRequesterInPersonConsented: (state, action: PayloadAction<boolean>) => {
      state.requesterInPersonConsented = action.payload;
    },
  },
});

const checkApprovalNeeded = (reviewers: RequestParticipant[]) => {
  if (reviewers.length === 0) {
    return false;
  }

  const hasValidReviewer = reviewers.some(
    (reviewer) => reviewer.name && reviewer.email,
  );

  return hasValidReviewer;
};

export const {
  setScreen,
  setCustomFields,
  upsertCustomField,
  removeCustomField,
  setLoading,
  setIsRequest,
  setReduxSignRequest,
  setPdfRequester,
  setReduxPdfApprovals,
  setReduxSelectedParticipant,
  setReduxSelectedFile,
  setSelectedField,
  selectField,
  updateIndividualSigner,
  moveIndividualSigner,
  updateIndividualReviewer,
  moveIndividualReviewer,
  deleteReviewer,
  setUploadedFileNames,
  setUploadedFileNamesDelete,
  updateFieldOfSelectedFile,
  updateFieldInSignRequest,
  setRadioGroupValue,
  assignExistingFieldsToSigner,
  deleteFieldOfSelectedFile,
  updateFileInSignRequest,
  updateFileWithFields,
  addFileInSignRequest,
  removeFileFromSignRequest,
  setErrors,
  deleteSigner,
  removeFieldsOfCC,
  updateSelfSignedFlag,
  updateStatusBulkSend,
  setUploadedCsvFileName,
  addAllErrorCsvSigner,
  updateIndividualErrorSigner,
  deleteErrorSigner,
  setContacts,
  setScheduleTime,
  setIsDriveIntegrated,
  setDriveFiles,
  setIsDrive,
  setDriveAccessToken,
  setIntegrations,
  setSelectedFieldDrawer,
  setMobilePropertiesRequested,
  setAssignExistingFields,
  moveFileSignRequest,
  setGoBackPopup,
  setChipLoader,
  setSideCollpased,
  moveGroup,
  moveReviewerGroup,
  moveSignerBlock,
  updateSignerOrders,
  moveReviewerBlock,
  mergeReviewerToGroup,
  updateReviewerOrders,
  deleteMultipleReviewer,
  updateMultipleSigners,
  pushHistorySnapshot,
  undoFieldChange,
  redoFieldChange,
  clearFieldHistory,
  setClipboardField,
  setHasAutoDetected,
  setPendingAiFields,
  clearPendingAiFields,
  setRequesterInPersonConsented,
} = appSlice.actions;

export const selectScreen = (state: RootState) => state.app.isMobile;

export default appSlice.reducer;
